import { useMemo } from "react";
import { fmtAed } from "./KpiCard";
import DataTable from "./DataTable";
import CollapsibleSection from "./CollapsibleSection";
import EmptyState from "./EmptyState";
import { normaliseNeedle, rowMatchesTextAt } from "../lib/textSearch";
import type { FrameRecords, SideDisplayConfig } from "../types/api";

interface Props {
  frame: FrameRecords;
  isCri: boolean;
  filters: RowFilter;
  config?: SideDisplayConfig;
  // Scopes pinned columns. Unused for the CRI side, whose pins are shared across
  // insurers (one export format).
  insurerName?: string;
  // flat: one table of raw transactions with a leading Match Status column instead of
  // per-section grouping (the CRI/Insurer statement tabs use this).
  flat?: boolean;
}

export interface RowFilter {
  categories: string[];
  statuses: string[];
  criTypes: string[];
  insurerTypes: string[];
  lob: string[];
  // Which `period_position` values to show. DEFAULTS to ["In period"] so the tables
  // open on the current period's activity alone — the out-of-window rows are still
  // there (the engine never removes a row), one untick away. Empty = show every side.
  periods: string[];
  dateFrom: string; // ISO yyyy-mm-dd, "" = no lower bound
  dateTo: string;   // ISO yyyy-mm-dd, "" = no upper bound
  text: string;
  // The data's FULL date span, so a tab can tell "user narrowed the range" from
  // "inputs are sitting at their defaults". Undated rows are only excluded in the
  // former case — see the date filter in StatementTab.
  spanFrom?: string;
  spanTo?: string;
}

// Has the user actually narrowed the date range away from the full data span? The
// From/To inputs default to that span, so a bound equal to it excludes nothing.
export function isDateNarrowed(f: RowFilter): boolean {
  const fromNarrowed = Boolean(f.dateFrom) && (!f.spanFrom || f.dateFrom > f.spanFrom);
  const toNarrowed = Boolean(f.dateTo) && (!f.spanTo || f.dateTo < f.spanTo);
  return fromNarrowed || toNarrowed;
}

// First 10 chars of an ISO-serialised doc_date, or "" when the cell isn't a date.
// (The API serialises dates via isoformat, so lexicographic compare is date order.)
function isoDay(v: string | number | boolean | null): string {
  const s = String(v ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
}

// Does this row survive `filters`? The single definition of the display filters,
// shared by the tabs that render rows and by the tab-strip match counts, so a count
// can never disagree with what the tab actually shows.
//
// Returns a PREDICATE rather than testing one row, because every column position it
// needs is invariant across the rows of one frame. Resolving them per row cost seven
// `columns.indexOf` linear scans per row — on Dubai's 62,384-row insurer frame, ~437k
// scans over 53 columns for a single pass (measured 28.3ms; 9.7ms this way, same
// 12,978 rows). Same shape SideBySideTab::filterPositions already uses.
export function makeRowFilter(
  frame: FrameRecords,
  filters: RowFilter,
): (ri: number) => boolean {
  const i = (name: string) => frame.columns.indexOf(name);
  const cCat = i("recon_category");
  // No category column => no row passes, exactly as the per-row form returned false.
  if (cCat < 0) return () => false;
  const cStatus = i("match_status");
  const cCriType = i("cri_type");
  const cInsType = i("role_legacy");
  const cLob = i("line_of_business");
  // Period side. Skipped when the frame predates the column (an older cached result),
  // so a stale payload shows every row rather than silently rendering an empty table.
  const cPeriod = i("period_position");
  const cDate = i("doc_date");
  const needle = normaliseNeedle(filters.text);
  const narrowed = isDateNarrowed(filters);
  const dateBounded = Boolean(filters.dateFrom || filters.dateTo);
  return (ri: number): boolean => {
    const r = frame.rows[ri];
    if (filters.categories.length && !filters.categories.includes(String(r[cCat] ?? ""))) return false;
    if (filters.statuses.length && cStatus >= 0 && !filters.statuses.includes(String(r[cStatus] ?? ""))) return false;
    if (filters.criTypes.length && cCriType >= 0 && !filters.criTypes.includes(String(r[cCriType] ?? ""))) return false;
    if (filters.insurerTypes.length && cInsType >= 0 && !filters.insurerTypes.includes(String(r[cInsType] ?? ""))) return false;
    if (filters.lob.length && cLob >= 0 && !filters.lob.includes(String(r[cLob] ?? ""))) return false;
    if (filters.periods.length && cPeriod >= 0
        && !filters.periods.includes(String(r[cPeriod] ?? ""))) return false;
    if (dateBounded && cDate >= 0) {
      const d = isoDay(r[cDate]);
      if (!d) {
        if (narrowed) return false;
      } else {
        if (filters.dateFrom && d < filters.dateFrom) return false;
        if (filters.dateTo && d > filters.dateTo) return false;
      }
    }
    if (needle && !rowMatchesTextAt(frame, ri, needle)) return false;
    return true;
  };
}

// How many rows of `frame` survive `filters` — the tab-strip match counts.
export function countMatchingRows(frame: FrameRecords, filters: RowFilter): number {
  const keep = makeRowFilter(frame, filters);
  let n = 0;
  for (let ri = 0; ri < frame.rows.length; ri++) {
    if (keep(ri)) n++;
  }
  return n;
}

// Statement tab. Default (flat): ONE table of the raw transactions — no per-category
// grouping. Sectioned mode keeps the collapsible per-category sections with subtotals.
// Both are pure display over columns already in the frame (recon_category + the amount
// column) — no reconciliation math. recon_category still drives the sectioning here;
// it is just no longer rendered as a column of its own.
type Pair = { r: FrameRecords["rows"][number]; i: FrameRecords["indices"][number] };

export default function StatementTab({ frame, isCri, filters, config, flat, insurerName = "" }: Props) {
  const idx = (name: string) => frame.columns.indexOf(name);
  const amountCol = isCri
    ? "cri_amount"
    : frame.columns.includes("balance_amount")
      ? "balance_amount"
      : "signed_amount";

  // Apply the display filters (same fields as the Streamlit filter row), keeping
  // each surviving row paired with its original index.
  const filtered = useMemo(() => {
    if (idx("recon_category") < 0) return [] as Pair[];

    const keep = makeRowFilter(frame, filters);
    const kept: Pair[] = [];
    frame.rows.forEach((r, ri) => {
      if (!keep(ri)) return;
      kept.push({ r, i: frame.indices[ri] });
    });
    return kept;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame, filters]);

  // Flat mode: single frame slice; the Match Status column leads the curated column
  // order so every row shows its status first (Category no longer leads).
  const flatFrame = useMemo<FrameRecords>(() => ({
    columns: frame.columns,
    rows: filtered.map((p) => p.r),
    indices: filtered.map((p) => p.i),
    total_rows: filtered.length,
  }), [frame, filtered]);

  const flatConfig = useMemo<SideDisplayConfig | undefined>(() => {
    if (!config) return config;
    return {
      columns: ["match_status", ...config.columns.filter((c) => c !== "match_status")],
      renames: { ...config.renames, match_status: "Match Status" },
    };
  }, [config]);

  // Sectioned mode: bucket rows by recon_category. Subtotal = sum of the amount
  // column; category buckets sort alphabetically.
  const sections = useMemo(() => {
    if (flat) return [];
    const cCat = idx("recon_category");
    const cAmt = idx(amountCol);
    if (cCat < 0) return [];

    const buckets = new Map<string, Pair[]>();
    for (const p of filtered) {
      const key = String(p.r[cCat] ?? "");
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(p);
    }
    const keys = [...buckets.keys()].sort();
    return keys.map((cat) => {
      const grp = buckets.get(cat)!;
      const subtotal = cAmt >= 0
        ? grp.reduce((s, p) => s + (typeof p.r[cAmt] === "number" ? (p.r[cAmt] as number) : 0), 0)
        : 0;
      const frameSlice: FrameRecords = {
        columns: frame.columns,
        rows: grp.map((p) => p.r),
        indices: grp.map((p) => p.i),
        total_rows: grp.length,
      };
      return { cat, subtotal, count: grp.length, frame: frameSlice };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat, filtered, frame, amountCol]);

  const pinSide = isCri ? "cri" : "insurer";

  if (flat) {
    if (!filtered.length) return <EmptyState title="No items match the current filters"
      body="Every row in this tab is filtered out. Clear the date range or search box above to see them again." />;
    return <DataTable frame={flatFrame} pageSize={100} config={flatConfig}
      pinSide={pinSide} insurerName={insurerName} title={isCri ? "CRI statement" : "Insurer statement"} />;
  }

  if (!sections.length) return <EmptyState title="No items match the current filters"
      body="Every row in this tab is filtered out. Clear the date range or search box above to see them again." />;

  return (
    <div>
      {sections.map((sec) => (
        <CollapsibleSection
          key={sec.cat}
          title={sec.cat}
          sub={`${fmtAed(sec.subtotal)}   (${sec.count.toLocaleString()} items)`}
          defaultOpen={sec.cat === "Matched"}
        >
          <DataTable frame={sec.frame} pageSize={50} config={config}
            pinSide={pinSide} insurerName={insurerName} title={sec.cat} />
        </CollapsibleSection>
      ))}
    </div>
  );
}
